import { useState } from "react";
import { DownOutlined } from "@ant-design/icons";
import type { MenuProps } from "antd";
import { Dropdown, Space } from "antd";
import { FeedPosts } from "../../Config/interface.config";
import {
  postDelete,
  requestPostRemovalReview,
} from "../../Redux/slices/postSlice";
import { useDispatch } from "react-redux";
import { AppDispatch } from "../../Redux/store";
import CreatePostModal from "../../Modals/CreatePostModal";
import AppConfirmationPrompt from "../AppConfirmationPrompt";
import toast from "react-hot-toast";

interface Props {
  post?: FeedPosts;
}

interface RemovalRestriction {
  code?: string;
  message?: string;
  nextAvailableAt?: string | null;
  limit?: number;
  windowDays?: number;
}

const PostAction = ({ post }: Props) => {
  const [open, setOpen] = useState<boolean>(false);
  const [safetyOpen, setSafetyOpen] = useState<boolean>(false);
  const [restriction, setRestriction] = useState<RemovalRestriction | null>(null);
  const [requestingReview, setRequestingReview] = useState(false);
  const dispatch: AppDispatch = useDispatch();
  const [createPostModal, setCreatePostModal] = useState(false);

  const toggleModal = () => setOpen(!open);

  const items: MenuProps["items"] = [
    {
      key: "1",
      label: (
        <div onClick={() => handleEditPost()}>
          <p className="text-white mb-0">Edit Post</p>
        </div>
      ),
    },
    {
      key: "2",
      label: (
        <div onClick={toggleModal}>
          <p className="text-white mb-0">Delete Post</p>
        </div>
      ),
    },
  ];

  const deletePosthandler = async () => {
    if (typeof post?._id !== "string") return;

    const result = await dispatch(postDelete({ postId: post._id }));

    if (postDelete.rejected.match(result)) {
      const error = (result.payload ?? {}) as RemovalRestriction;

      if (error.code === "POST_REMOVAL_TEMPORARILY_RESTRICTED") {
        setOpen(false);
        setRestriction(error);
        setSafetyOpen(true);
        return;
      }

      toast.error(error.message ?? "Unable to remove post.");
      return;
    }

    setOpen(false);
  };

  const requestRemoval = async (urgent: boolean) => {
    if (requestingReview || typeof post?._id !== "string") return;

    setRequestingReview(true);
    const result = await dispatch(
      requestPostRemovalReview({
        postId: post._id,
        urgent,
      })
    );
    setRequestingReview(false);

    if (requestPostRemovalReview.rejected.match(result)) {
      const error = (result.payload ?? {}) as RemovalRestriction;
      toast.error(error.message ?? "Unable to send removal request.");
      return;
    }

    setSafetyOpen(false);
    setRestriction(null);
  };

  const toggleCreatePostModal = () => setCreatePostModal(!createPostModal);

  const handleEditPost = () => {
    if (typeof post?._id !== "string" || typeof post.text !== "string") return;
    toggleCreatePostModal();
  };

  const nextAvailableText = restriction?.nextAvailableAt
    ? ` Normal removal will automatically become available again after ${new Date(
        restriction.nextAvailableAt
      ).toLocaleString()}.`
    : " Normal removal will automatically become available again as older removals fall outside the 7-day safety window.";

  return (
    <>
      <Dropdown menu={{ items }} placement="bottomRight" rootClassName="fanxo-dropdown">
        <Space>
          <DownOutlined className="text-white" />
        </Space>
      </Dropdown>

      <AppConfirmationPrompt
        show={open}
        title="Delete Post?"
        body="This will remove the post from Fanxo."
        confirmText="Delete"
        cancelText="Cancel"
        onClose={() => setOpen(false)}
        onConfirm={() => deletePosthandler()}
        onCancel={() => setOpen(false)}
      />

      <AppConfirmationPrompt
        show={safetyOpen}
        title="Post removal temporarily restricted"
        body={
          <>
            <span className="block">
              {restriction?.message ??
                "Several premium posts were recently removed. Additional removals are temporarily restricted to protect your account and subscribers."}
              {nextAvailableText}
            </span>
          </>
        }
        confirmText={requestingReview ? "Sending..." : "Request deletion unlock"}
        cancelText="Keep post"
        onClose={() => setSafetyOpen(false)}
        onConfirm={() => requestRemoval(false)}
        onCancel={() => setSafetyOpen(false)}
      />

      <CreatePostModal
        onHide={() => toggleCreatePostModal()}
        show={createPostModal}
        key={"edit-post-modal"}
        editPost={post}
      />
    </>
  );
};

export default PostAction;
